export type TunnelStatus = { url: string | null; isActive: boolean }

/** How soon to ask again while the tunnel has no URL yet. */
export const TUNNEL_PENDING_POLL_MS = 5_000
/** How often to re-check once a URL is known, to notice the tunnel dying. */
export const TUNNEL_READY_POLL_MS = 60_000

/**
 * Keep asking the main process for the print-server tunnel until it has a URL,
 * then keep checking it, and report every answer.
 *
 * This used to be asked exactly once, when the booth page mounted. The main
 * process starts cloudflared in parallel with the window, and a quick tunnel
 * takes ~6 s to hand out its URL, so a page that mounted first read `null` and
 * never asked again. With no URL the booth never registers as a remote
 * printer: every tablet printing through it lost the printer for the whole day,
 * until someone happened to restart the app (Bahela Cikstik, 2026-10-08 — app
 * up at 09:38, no registration, a paid karcis at 10:12 printed nothing).
 *
 * The slow re-check matters too: when cloudflared exits, the main process
 * clears the URL. Seeing that stops the registration heartbeat, so tablets get
 * "not registered" instead of firing jobs at a tunnel that no longer exists.
 *
 * Only `get-tunnel-status` is used, which every shell has — this reaches booths
 * on old shells without a release.
 */
export function watchTunnelStatus(
  fetchStatus: () => Promise<TunnelStatus>,
  onStatus: (status: TunnelStatus) => void
): () => void {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | null = null

  const poll = async (): Promise<void> => {
    let status: TunnelStatus
    try {
      status = await fetchStatus()
    } catch (error) {
      console.error('Failed to fetch tunnel status:', error)
      status = { url: null, isActive: false }
    }
    if (stopped) return
    onStatus(status)
    timer = setTimeout(
      () => void poll(),
      status.url ? TUNNEL_READY_POLL_MS : TUNNEL_PENDING_POLL_MS
    )
  }

  void poll()

  return (): void => {
    stopped = true
    if (timer) clearTimeout(timer)
  }
}
